<template>
  <div class="fee-cost-table">
    <BjPage ref="BjPage" :query-params="queryParams" :get-action="queryApi" hide-search :selection="selection" :serial="serial" @handleSelectionChange="handleSelectionChange" @showSetting="SortableTable.show" @header-dragend="SortableTable.handleHeaderDragend" @header-contextmenu="SortableTable.handleHeaderContextmenu">
      <template v-slot:tableOperation="tableOperationProp">
        <el-col>
          <el-button v-if="isDetail" type="primary" @click="onAdd">{{ $t('addText') }}</el-button>
          <el-button v-if="isDetail" type="primary" :disabled="tableOperationProp.notSelected" @click="onDeleteSelected">{{ $t('bulkDelText') }}</el-button>
          <el-button v-if="isDetail" type="primary" :disabled="tableOperationProp.notSelected || !canBatchConfirmSelected" @click="onBatchConfirm">{{ $t('feeTab.batchConfirm') }}</el-button>
          <el-button v-if="isDetail" type="primary" :disabled="tableOperationProp.notSelected || !canCancelConfirmSelected" @click="onCancelConfirm">{{ $t('feeTab.cancelConfirm') }}</el-button>
          <BjFrontendExport
            type="primary"
            :disabled="tableOperationProp.notSelected"
            :label-list="exportLabelList"
            :rows="selectedRows"
            :file-name="fileName"
            @export-success="clearSelectedRows"
          />
          <el-button v-if="isSummary" type="primary" @click="onRefreshOrder">{{ $t('feeTab.recalculateFee') }}</el-button>
        </el-col>
      </template>
      <template v-slot:tableColumn>
        <template v-for="(col, index) in visibleDropCols">
          <el-table-column v-if="col.prop !== 'bjTableAction'" :key="`col_${index}_${col.prop}_${col.width || 'auto'}`" :prop="col.prop" :label="col.label" :width="col.width || 'auto'" :fixed="col.fixed || false" align="center" class-name="item">
            <template slot-scope="scope">
              <template v-if="col.prop === 'feeItem'">{{ feeItemList.bjGet(scope.row[col.prop]) }}</template>
              <template v-else-if="col.prop === 'chargeUnit'">{{ chargeUnitList.bjGet(scope.row[col.prop]) }}</template>
              <template v-else-if="col.prop === 'status'">{{ $t(FEE_STATUS_LIST.bjGet(scope.row[col.prop])) }}</template>
              <template v-else-if="col.prop === 'verificationStatus'">{{ $t(FEE_VERIFICATION_STATUS_LIST.bjGet(scope.row[col.prop])) }}</template>
              <template v-else-if="col.prop === 'source'">{{ formatSourceValue(scope.row[col.prop]) }}</template>
              <template v-else-if="col.digit !== undefined">{{ formatDecimalValue(scope.row[col.prop], col.digit) }}</template>
              <template v-else>{{ scope.row[col.prop] }}</template>
            </template>
          </el-table-column>
          <el-table-column v-if="col.prop === 'bjTableAction'" :key="`col_${index}_${col.prop}_${col.width || 'auto'}`" :prop="col.prop" :label="col.label" :width="col.width || 'auto'" :fixed="col.fixed || 'right'" align="center">
            <template slot-scope="props">
              <el-button type="text" :disabled="!canEditRow(props.row)" @click="onEdit(props.row)">{{ $t('editText') }}</el-button>
            </template>
          </el-table-column>
        </template>
      </template>
    </BjPage>
    <FeeFormDialog v-if="isDetail" ref="feeFormDialog" :fee-name-options="feeItemList" :charge-unit-options="chargeUnitList" :outer-order-no="outerOrderNo" :type="type" @saved="handleSaved" />
  </div>
</template>

<script>
import { logisticApi } from '@/api';
import pageMiXin from '@/utils/page-mixin.js';
import FeeFormDialog from './FeeFormDialog.vue';

export default {
  name: 'FeeCostTable',
  components: { FeeFormDialog },
  mixins: [pageMiXin],
  props: {
    outerOrderNo: {
      type: String,
      default: '',
    },
    type: {
      type: Number,
      default: 0,
    },
    mode: {
      type: String,
      default: 'detail',
      validator: value => ['summary', 'detail'].includes(value),
    },
    fileName: {
      type: String,
      default: 'Fee.csv',
    },
    feeItemList: {
      type: Array,
      default: () => [],
    },
    chargeUnitList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const checkStatusAndSource = this.mode !== 'summary';
    return {
      serial: false,
      sortTableByOptionName: true,
      FEE_STATUS_LIST: [
        { label: 'feeTab.status0', value: 0 },
        { label: 'feeTab.status1', value: 1 },
      ],
      FEE_VERIFICATION_STATUS_LIST: [
        { label: 'feeTab.verificationStatus0', value: 0 },
        { label: 'feeTab.verificationStatus1', value: 1 },
      ],
      FEE_SOURCE_LIST: [
        { label: 'feeTab.sourceSYSTEM', value: 'SYSTEM' },
        { label: 'feeTab.sourceMANUAL', value: 'MANUAL' },
      ],
      labelList: [
        { label: this.$t('feeTab.feeName'), prop: 'feeItem', isCheck: true, disabled: false, width: 140 },
        { label: this.$t('feeTab.settlementParty'), prop: 'cusName', isCheck: true, disabled: false, width: 140 },
        { label: this.$t('feeTab.billingUnit'), prop: 'chargeUnit', isCheck: true, disabled: false, width: 100 },
        { label: this.$t('feeTab.settlementUnitPrice'), prop: 'unitPrice', isCheck: true, disabled: false, width: 120, digit: 4 },
        { label: this.$t('feeTab.quantity'), prop: 'settlementNum', isCheck: true, disabled: false, width: 90, digit: 3 },
        { label: this.$t('feeTab.settlementCurrency'), prop: 'quotationCurrency', isCheck: true, disabled: false, width: 110 },
        { label: this.$t('feeTab.settlementAmount'), prop: 'billingAmount', isCheck: true, disabled: false, width: 120, digit: 2 },
        { label: this.$t('feeTab.baseCurrency'), prop: 'standardCurrency', isCheck: true, disabled: false, width: 100 },
        { label: this.$t('feeTab.exchangeRate'), prop: 'exchangeRate', isCheck: true, disabled: false, width: 90 },
        { label: this.$t('feeTab.baseCurrencyAmount'), prop: 'standardAmount', isCheck: true, disabled: false, width: 120, digit: 2 },
        { label: this.$t('feeTab.status'), prop: 'status', isCheck: checkStatusAndSource, disabled: false, width: 100 },
        { label: this.$t('feeTab.writeOffStatus'), prop: 'verificationStatus', isCheck: true, disabled: false, width: 110 },
        { label: this.$t('feeTab.source'), prop: 'source', isCheck: checkStatusAndSource, disabled: false, width: 100 },
        { label: this.$t('feeTab.systemCalculatedAmount'), prop: 'quotationAmount', isCheck: true, disabled: false, width: 140, digit: 2 },
        { label: this.$t('feeTab.apiAmount'), prop: 'apiAmount', isCheck: true, disabled: false, digit: 2 },
        { label: this.$t('feeTab.invoiceAmount'), prop: 'invoiceAmount', isCheck: true, disabled: false, digit: 2 },
        { label: this.$t('feeTab.trackingNumber'), prop: 'trackingNumber', isCheck: false, disabled: false, width: 140 },
        { label: this.$t('feeTab.packageNo'), prop: 'packageNo', isCheck: false, disabled: false, width: 120 },
        { label: this.$t('table.operationText'), prop: 'bjTableAction', isCheck: true, disabled: false, width: 100 },
      ],
    };
  },
  computed: {
    isDetail() {
      return this.mode === 'detail';
    },
    isSummary() {
      return this.mode === 'summary';
    },
    queryParams() {
      return {
        outerOrderNo: this.outerOrderNo,
        type: this.type,
      };
    },
    queryApi() {
      return this.isDetail ? logisticApi.getOrderQuateDetail : logisticApi.getOrderQuateCost;
    },
    canBatchConfirmSelected() {
      return this.selectedRows.length > 0 && this.selectedRows.every(row => this.isManualPending(row));
    },
    canCancelConfirmSelected() {
      return this.selectedRows.length > 0 && this.selectedRows.every(row => this.isManualConfirmed(row));
    },
    visibleDropCols() {
      if (this.isDetail) return this.dropCols;
      return this.dropCols.filter(col => col.prop !== 'bjTableAction');
    },
    exportLabelList() {
      const optionConfigs = {
        feeItem: { options: () => this.feeItemList },
        chargeUnit: { options: () => this.chargeUnitList },
        status: { options: this.FEE_STATUS_LIST, translate: true },
        verificationStatus: { options: this.FEE_VERIFICATION_STATUS_LIST, translate: true },
        source: { options: this.FEE_SOURCE_LIST, translate: true, optionFallback: 'raw' },
      };
      return this.labelList.map(column => {
        const exportConfig = optionConfigs[column.prop] ||
          (column.digit === undefined ? {} : { formatter: value => this.formatDecimalValue(value, column.digit) });
        return { ...column, export: exportConfig };
      });
    },
  },
  methods: {
    isManualPending(row) {
      return row.source == 'MANUAL' && row.status == 0;
    },
    isManualConfirmed(row) {
      return row.source == 'MANUAL' && row.status == 1;
    },
    canEditRow(row) {
      return this.isManualPending(row);
    },
    formatDecimalValue(value, digit) {
      if (value === undefined || value === null || value === '') return '';
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? value : numberValue.toFixed(digit);
    },
    formatSourceValue(value) {
      if (value === undefined || value === null || value === '') return '';
      const label = this.FEE_SOURCE_LIST.bjGet(value);
      return label ? this.$t(label) : value;
    },
    clearSelectedRows() {
      this.$refs.BjPage && this.$refs.BjPage.handleAllTagsClose();
      this.selectedRows = [];
    },
    handleOperationSuccess(res) {
      if (res.id || res._token || res.content || Array.isArray(res) || res.failed === false) {
        this.$message.success(this.$t('toast.operationSuccessText'));
        this.clearSelectedRows();
        this.reFresh();
        this.$emit('refresh-card');
      }
    },
    onDeleteSelected() {
      const ids = this.selectedRows.map(e => e.id);
      this.$confirm(this.$t('toast.delAllTipText'), this.$t('toast.hintText'), {
        confirmButtonText: this.$t('toast.okText'),
        cancelButtonText: this.$t('toast.cancelText'),
        type: 'warning',
      })
        .then(() => {
          logisticApi.delOrderQuateCost({ ids }).then(res => {
            this.handleOperationSuccess(res);
          });
        })
        .catch(() => {});
    },
    onBatchConfirm() {
      const ids = this.selectedRows.map(e => e.id);
      logisticApi.doneOrderQuateCost({ ids, status: 1 }).then(res => {
        this.handleOperationSuccess(res);
      });
    },
    onCancelConfirm() {
      const ids = this.selectedRows.map(e => e.id);
      logisticApi.doneOrderQuateCost({ ids, status: 0 }).then(res => {
        this.handleOperationSuccess(res);
      });
    },
    onRefreshOrder() {
      logisticApi.refreshOrderQuateCost(this.outerOrderNo).then(res => {
        this.handleOperationSuccess(res);
      });
    },
    onAdd() {
      this.$refs.feeFormDialog.open();
    },
    onEdit(row) {
      this.$refs.feeFormDialog.open(row);
    },
    handleSaved() {
      this.reFresh();
      this.$emit('refresh-card');
    },
  },
};
</script>
